export const STATIC_PROFILE_VERSION = 1;
export const STATIC_TEMPLATE_SCHEMA_VERSION = 2;
export const SCREEN_PROFILE_VERSION = 1;
export const MOTION_PROFILE_VERSION = 1;
export const SINGLE_SCREEN_PROFILE = {
    id: 'single-screen',
    version: SCREEN_PROFILE_VERSION
};
export const DUAL_SCREEN_PROFILE = {
    id: 'dual-screen',
    version: SCREEN_PROFILE_VERSION
};
export function normalizeScreenProfileRef(value) {
    if (!value || typeof value !== 'object') {
        return { ...SINGLE_SCREEN_PROFILE };
    }
    const candidate = value;
    if ((candidate.id === 'single-screen' || candidate.id === 'dual-screen') &&
        Number(candidate.version) === SCREEN_PROFILE_VERSION) {
        return { id: candidate.id, version: SCREEN_PROFILE_VERSION };
    }
    return { ...SINGLE_SCREEN_PROFILE };
}
export function isDualScreenProfile(value) {
    return normalizeScreenProfileRef(value).id === 'dual-screen';
}
export function getScreenSlots(value) {
    const dualScreen = isDualScreenProfile(value);
    return [
        { id: 'screen-1', label: 'Screen 1', order: 0, zIndex: 0, defaultFit: 'cover' },
        ...(dualScreen
            ? [{ id: 'screen-2', label: 'Screen 2', order: 1, zIndex: 1, defaultFit: 'cover' }]
            : [])
    ];
}
export function normalizeMotionProfileRef(value) {
    if (value && typeof value === 'object') {
        const candidate = value;
        if ((candidate.id === 'motion.single-screen' || candidate.id === 'motion.dual-screen') &&
            Number(candidate.version) === MOTION_PROFILE_VERSION) {
            return { id: candidate.id, version: MOTION_PROFILE_VERSION };
        }
    }
    return { id: 'motion.single-screen', version: MOTION_PROFILE_VERSION };
}
export function resolveMotionAtlasAuxRole(options) {
    if (options.atlasLayout === 'extended-6') {
        return 'alternate-screen-mask';
    }
    if (options.motionDynamicIslandToggle === true) {
        return 'alternate-screen-mask';
    }
    return options.screenBlurEnabled === false ? 'ignored' : 'screen-blur-mask';
}
export function isMultiScreenTrackingDocument(value) {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value;
    if (candidate.format !== 'mockupCreator.multiScreenSequence' ||
        candidate.version !== 2 ||
        !Number.isFinite(candidate.duration) ||
        Number(candidate.duration) <= 0 ||
        !Number.isFinite(candidate.fps) ||
        Number(candidate.fps) <= 0 ||
        !Number.isInteger(candidate.frameCount) ||
        Number(candidate.frameCount) <= 0 ||
        !Array.isArray(candidate.tracks) ||
        candidate.tracks.length !== 2) {
        return false;
    }
    const ids = candidate.tracks.map((track) => track?.id);
    const structurallyValid = (ids.includes('screen-1') &&
        ids.includes('screen-2') &&
        candidate.tracks.every((track) => Array.isArray(track.frames) &&
            track.frames.length === candidate.frameCount &&
            track.frames.every((frame) => Number.isFinite(frame?.t) &&
                Array.isArray(frame?.points) &&
                frame.points.length === 4 &&
                frame.points.every((point) => Number.isFinite(point?.x) && Number.isFinite(point?.y)))));
    if (!structurallyValid) {
        return false;
    }
    const firstTrack = candidate.tracks.find((track) => track.id === 'screen-1');
    const secondTrack = candidate.tracks.find((track) => track.id === 'screen-2');
    let previousTime = Number.NEGATIVE_INFINITY;
    for (let index = 0; index < Number(candidate.frameCount); index += 1) {
        const firstTime = firstTrack.frames[index].t;
        const secondTime = secondTrack.frames[index].t;
        if (firstTime < 0 || firstTime <= previousTime || Math.abs(firstTime - secondTime) > 0.0005) {
            return false;
        }
        previousTime = firstTime;
    }
    return true;
}
export function getMotionPlaybackDuration(templateDuration, userVideoDuration, loopEnabled) {
    const normalizedTemplateDuration = normalizePositiveMotionDuration(templateDuration);
    const normalizedUserVideoDuration = normalizePositiveMotionDuration(userVideoDuration);
    const effectiveDuration = Math.max(normalizedTemplateDuration, normalizedUserVideoDuration);
    if (!loopEnabled || normalizedTemplateDuration <= 0 || normalizedUserVideoDuration <= normalizedTemplateDuration) {
        return effectiveDuration;
    }
    return Math.ceil(normalizedUserVideoDuration / normalizedTemplateDuration) * normalizedTemplateDuration;
}
export function getMotionTemplatePlaybackTime(templateDuration, playbackTime, loopEnabled) {
    const normalizedTemplateDuration = normalizePositiveMotionDuration(templateDuration);
    const normalizedPlaybackTime = Number.isFinite(playbackTime) ? Math.max(0, playbackTime) : 0;
    if (normalizedTemplateDuration <= 0) {
        return 0;
    }
    if (!loopEnabled) {
        return Math.min(normalizedPlaybackTime, normalizedTemplateDuration);
    }
    return normalizedPlaybackTime % normalizedTemplateDuration;
}
function normalizePositiveMotionDuration(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : 0;
}
export function compareMotionTimings(left, right, options = {}) {
    const leftLabel = options.leftLabel || 'First source';
    const rightLabel = options.rightLabel || 'Second source';
    const maxFrameDelta = Math.max(0, Math.floor(options.maxFrameDelta ?? 1));
    const issues = [
        ...validateMotionTimingDescriptor(left, leftLabel),
        ...validateMotionTimingDescriptor(right, rightLabel)
    ];
    if (issues.some((issue) => issue.severity === 'error')) {
        return issues;
    }
    if (Math.abs(left.fps - right.fps) > 0.01) {
        issues.push({
            severity: 'error',
            field: 'fps',
            message: `${leftLabel}/${rightLabel} FPS mismatch: ${formatMotionMetric(left.fps)} and ${formatMotionMetric(right.fps)}.`
        });
    }
    const durationTolerance = 1 / Math.max(1, Math.min(left.fps, right.fps)) + 0.002;
    if (Math.abs(left.duration - right.duration) > durationTolerance) {
        issues.push({
            severity: 'error',
            field: 'duration',
            message: `${leftLabel}/${rightLabel} duration mismatch: ${formatMotionMetric(left.duration)}s and ${formatMotionMetric(right.duration)}s.`
        });
    }
    const frameDelta = Math.abs(left.frameCount - right.frameCount);
    if (frameDelta > maxFrameDelta) {
        issues.push({
            severity: 'error',
            field: 'frameCount',
            message: `${leftLabel}/${rightLabel} frame count mismatch: ${left.frameCount} and ${right.frameCount}.`
        });
    }
    else if (frameDelta > 0) {
        issues.push({
            severity: 'warning',
            field: 'frameCount',
            message: `${leftLabel} and ${rightLabel} differ by ${frameDelta} frame${frameDelta === 1 ? '' : 's'} (${left.frameCount}/${right.frameCount}).`
        });
    }
    return issues;
}
function validateMotionTimingDescriptor(timing, label) {
    const issues = [];
    if (!Number.isFinite(timing.duration) || timing.duration <= 0) {
        issues.push({ severity: 'error', field: 'duration', message: `${label} duration must be a positive number.` });
    }
    if (!Number.isFinite(timing.fps) || timing.fps <= 0) {
        issues.push({ severity: 'error', field: 'fps', message: `${label} FPS must be a positive number.` });
    }
    if (!Number.isInteger(timing.frameCount) || timing.frameCount <= 0) {
        issues.push({
            severity: 'error',
            field: 'frameCount',
            message: `${label} frame count must be a positive integer.`
        });
    }
    return issues;
}
function formatMotionMetric(value) {
    return Number(value.toFixed(4)).toString();
}
export const STATIC_PROFILE_IDS = [
    'static.standard',
    'static.surface-reflection',
    'static.screen-blur',
    'static.dual-screen'
];
const DISABLED_CAPABILITIES = {
    screenCount: 1,
    diffuseColor: false,
    screenOverlayOpacity: false,
    dynamicIsland: false,
    backgroundTreatment: false,
    surfaceReflection: false,
    surfaceReflectionBlur: false,
    screenBlur: false
};
const BASE_DEFAULTS = {
    screenOverlayOpacity: 50,
    surfaceReflectionOpacity: 50,
    surfaceReflectionBlur: 50
};
const STATIC_PROFILE_REGISTRY = {
    'static.standard': {
        id: 'static.standard',
        version: STATIC_PROFILE_VERSION,
        label: 'Standard',
        description: 'Static screen mockup without a reflected screen on a surface.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenOverlay'],
        optionalAssets: ['screenMask2', 'backgroundMask'],
        requiredGeometry: ['screenQuad'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            diffuseColor: true,
            screenOverlayOpacity: true
        },
        defaults: BASE_DEFAULTS
    },
    'static.surface-reflection': {
        id: 'static.surface-reflection',
        version: STATIC_PROFILE_VERSION,
        label: 'Blur Reflection',
        description: 'Static screen mockup with a reflected copy on a floor or surface.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenOverlay'],
        optionalAssets: ['screenMask2', 'backgroundMask', 'reflectionMask', 'reflectionBlurMask'],
        requiredGeometry: ['screenQuad', 'reflectionQuad'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            diffuseColor: true,
            screenOverlayOpacity: true,
            surfaceReflection: true,
            surfaceReflectionBlur: true
        },
        defaults: BASE_DEFAULTS
    },
    'static.screen-blur': {
        id: 'static.screen-blur',
        version: STATIC_PROFILE_VERSION,
        label: 'Blur Screen',
        description: 'Static screen mockup with mask-controlled blur applied only to inserted screen content.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenOverlay', 'screenBlurMask'],
        optionalAssets: ['screenMask2', 'backgroundMask'],
        requiredGeometry: ['screenQuad'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            diffuseColor: true,
            screenOverlayOpacity: true,
            screenBlur: true
        },
        defaults: BASE_DEFAULTS
    },
    'static.dual-screen': {
        id: 'static.dual-screen',
        version: STATIC_PROFILE_VERSION,
        label: 'Dual Screen',
        description: 'Static mockup with two independent screen inputs and placements.',
        enabled: true,
        requiredAssets: ['preview', 'diffuse', 'screenMask', 'screenMask2', 'screenOverlay'],
        optionalAssets: ['backgroundMask', 'screenBlurMask'],
        requiredGeometry: ['screenQuad', 'screenQuad2'],
        capabilities: {
            ...DISABLED_CAPABILITIES,
            screenCount: 2,
            diffuseColor: true,
            screenOverlayOpacity: true,
            screenBlur: true
        },
        defaults: BASE_DEFAULTS
    }
};
export function isMotionTemplateType(value) {
    const normalized = String(value || '').trim().toUpperCase().replace(/_/g, '-');
    return normalized === 'MOTION' || normalized === 'MOTION-CG' || normalized === 'MOTION-AI';
}
export function isStaticProfileId(value) {
    return typeof value === 'string' && STATIC_PROFILE_IDS.includes(value);
}
export function normalizeStaticProfileRef(value) {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const candidate = value;
    if (!isStaticProfileId(candidate.id) || Number(candidate.version) !== STATIC_PROFILE_VERSION) {
        return null;
    }
    return {
        id: candidate.id,
        version: STATIC_PROFILE_VERSION
    };
}
export function getStaticProfileDefinition(profile) {
    const id = typeof profile === 'string' ? profile : profile.id;
    return STATIC_PROFILE_REGISTRY[id];
}
export function listStaticProfileDefinitions(options = {}) {
    return STATIC_PROFILE_IDS
        .map((id) => STATIC_PROFILE_REGISTRY[id])
        .filter((profile) => options.includeDisabled === true || profile.enabled);
}
export function inferStaticProfile(template) {
    if (template.isVideo === true || isMotionTemplateType(template.templateType)) {
        return null;
    }
    const explicitProfile = normalizeStaticProfileRef(template.staticProfile);
    if (explicitProfile) {
        return explicitProfile;
    }
    if (hasAsset(template.screenBlurMask)) {
        return { id: 'static.screen-blur', version: STATIC_PROFILE_VERSION };
    }
    if (template.reflectionEnabled === true && isQuad(template.reflectionQuad)) {
        return { id: 'static.surface-reflection', version: STATIC_PROFILE_VERSION };
    }
    return { id: 'static.standard', version: STATIC_PROFILE_VERSION };
}
export function resolveStaticTemplateCapabilities(template) {
    const profile = inferStaticProfile(template);
    if (!profile) {
        return { ...DISABLED_CAPABILITIES };
    }
    const definition = getStaticProfileDefinition(profile);
    return {
        ...definition.capabilities,
        screenCount: definition.capabilities.screenCount,
        screenOverlayOpacity: definition.capabilities.screenOverlayOpacity &&
            hasAsset(template.overlay) &&
            !hasAsset(template.reflectionBlurMask),
        dynamicIsland: hasAsset(template.mask2),
        backgroundTreatment: hasAsset(template.backgroundMask),
        surfaceReflection: definition.capabilities.surfaceReflection && template.reflectionEnabled === true && isQuad(template.reflectionQuad),
        surfaceReflectionBlur: definition.capabilities.surfaceReflectionBlur && template.reflectionEnabled === true && isQuad(template.reflectionQuad),
        screenBlur: definition.capabilities.screenBlur && hasAsset(template.screenBlurMask)
    };
}
export function validateStaticProfileTemplate(template) {
    const profile = inferStaticProfile(template);
    if (!profile) {
        return [];
    }
    const definition = getStaticProfileDefinition(profile);
    const issues = [];
    if (!definition.enabled) {
        issues.push({ field: 'staticProfile', message: `${definition.label} is not enabled yet.` });
    }
    if (profile.id === 'static.surface-reflection' && template.reflectionEnabled !== true) {
        issues.push({ field: 'staticProfile', message: 'Blur Reflection requires surface reflection to be enabled.' });
    }
    if (profile.id !== 'static.surface-reflection' && template.reflectionEnabled === true) {
        issues.push({ field: 'staticProfile', message: `${definition.label} does not support surface reflection.` });
    }
    if (profile.id === 'static.dual-screen' && !isDualScreenProfile(template.screenProfile)) {
        issues.push({ field: 'staticProfile', message: 'Dual Screen requires the dual-screen screen profile.' });
    }
    if (profile.id !== 'static.dual-screen' && isDualScreenProfile(template.screenProfile)) {
        issues.push({ field: 'staticProfile', message: `${definition.label} cannot use the dual-screen screen profile.` });
    }
    for (const slot of definition.requiredAssets) {
        if (!hasAsset(readAssetSlot(template, slot))) {
            issues.push({ field: slot, message: `${slot} is required for ${definition.label}.` });
        }
    }
    for (const slot of definition.requiredGeometry) {
        if (!isQuad(template[slot])) {
            issues.push({ field: slot, message: `${slot} must contain four finite points.` });
        }
    }
    return issues;
}
function readAssetSlot(template, slot) {
    switch (slot) {
        case 'preview':
            return template.preview;
        case 'diffuse':
            return template.base;
        case 'screenMask':
            return template.mask;
        case 'screenMask2':
            return template.mask2;
        case 'screenOverlay':
            return template.overlay;
        case 'backgroundMask':
            return template.backgroundMask;
        case 'reflectionMask':
            return template.reflectionMask;
        case 'reflectionBlurMask':
            return template.reflectionBlurMask;
        case 'screenBlurMask':
            return template.screenBlurMask;
    }
}
function hasAsset(value) {
    return typeof value === 'string' ? value.trim().length > 0 : Boolean(value);
}
function isQuad(value) {
    return (Array.isArray(value) &&
        value.length === 4 &&
        value.every((point) => {
            if (!point || typeof point !== 'object') {
                return false;
            }
            const candidate = point;
            return Number.isFinite(Number(candidate.x)) && Number.isFinite(Number(candidate.y));
        }));
}
/* ------------------------------------------------------------------ *
 * Motion Puzzle Matte — independent recolouring of up to three regions
 *
 * A separate synchronous MP4 whose pure red, green and blue channels mark
 * three regions of the mockup. The atlas is deliberately untouched: its aux
 * tile is already spent on the blur mask or the alternate screen mask, so a
 * fourth role there would collide with existing templates.
 *
 * The channel value doubles as coverage. Where compression or motion blur
 * bleeds one channel into another both values fall, so the effect fades out
 * at the edge instead of needing a hard classification step.
 * ------------------------------------------------------------------ */
export const MOTION_COLOR_MATTE_VERSION = 1;
/** Order here is the order the user sees: Color 1, Color 2, Color 3. */
export const MOTION_COLOR_MATTE_CHANNELS = ['r', 'g', 'b'];
export function isMotionColorMatteChannel(value) {
    return typeof value === 'string' && MOTION_COLOR_MATTE_CHANNELS.includes(value);
}
/**
 * Returns null for anything a template published before this feature could
 * carry, so an older catalog entry stays byte-identical after a round trip.
 */
export function normalizeMotionColorMatte(value) {
    if (!value || typeof value !== 'object') {
        return null;
    }
    const candidate = value;
    if (Number(candidate.version) !== MOTION_COLOR_MATTE_VERSION) {
        return null;
    }
    const rawChannels = candidate.channels;
    if (!Array.isArray(rawChannels)) {
        return null;
    }
    // Keep the canonical r/g/b order regardless of how the entry was written,
    // and drop duplicates — the user-facing numbering has to be stable.
    const channels = MOTION_COLOR_MATTE_CHANNELS.filter((channel) => rawChannels.some((entry) => isMotionColorMatteChannel(entry) && entry === channel));
    if (channels.length === 0) {
        return null;
    }
    return { version: MOTION_COLOR_MATTE_VERSION, channels: [...channels] };
}
/** Label shown above each control: Color 1 … Color 3, in channel order. */
export function getMotionColorMatteLabel(matte, channel) {
    const index = matte.channels.indexOf(channel);
    return index < 0 ? '' : `Color ${index + 1}`;
}
